import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  COLOR_TIPO_EVENTO,
  ETIQUETAS_TIPO_EVENTO,
  NOMBRES_DIA_CORTOS,
  NOMBRES_MES,
  aISO,
  diaSemanaLunesPrimero,
  formatearCLP,
  sumarMeses,
  type CapaCalendario,
  type EventoCalendario,
  type TipoEvento,
} from '@lexicore/shared';
import {
  IconoCalendario,
  IconoCapas,
  IconoDerecha,
  IconoDescargar,
  IconoIzquierda,
  IconoReloj,
} from '../componentes/Iconos';
import { Cargando, ErrorCarga, Panel, TarjetaKpi, Vacio } from '../componentes/UI';
import { descargar } from '../lib/api';
import {
  useConsolaCalendario,
  usePerspectivaMultimes,
} from '../lib/consultas';
import { ModalDetalleEventoCalendario } from './calendario/ModalDetalleEventoCalendario';
import { useAvisos } from '../contextos/AvisosContext';

/** Fondo tenue a juego con el color de cada tipo de evento */
const FONDOS: Record<string, string> = {
  PLAZO_POSTULACION: 'var(--accent-blue-soft)',
  ENTREGA_COMPROMETIDA: 'var(--accent-purple-soft)',
  FIN_PROYECTO: 'var(--accent-green-soft)',
  VENCIMIENTO_FACTURA: 'var(--accent-yellow-soft)',
  COBRO_PROYECTADO: 'var(--accent-blue-soft)',
  HITO: 'var(--accent-purple-soft)',
  PAGO_PROVEEDOR: 'var(--accent-red-soft)',
  GASTO_RECURRENTE: 'var(--accent-red-soft)',
};

const CAPAS: { clave: CapaCalendario; etiqueta: string; icono?: string }[] = [
  { clave: 'TODOS', etiqueta: 'Todas las Capas' },
  { clave: 'DINERO', etiqueta: '💰 Flujo de Dinero' },
  { clave: 'OPERACIONES', etiqueta: '⚙️ Operaciones' },
  { clave: 'COBROS', etiqueta: '🟢 Cobros' },
  { clave: 'PAGOS', etiqueta: '🔴 Pagos' },
  { clave: 'HITOS', etiqueta: '🔷 Hitos' },
  { clave: 'LICITACIONES', etiqueta: '⚖️ Licitaciones' },
];

export function Calendario() {
  const avisosContext = useAvisos();
  const navegar = useNavigate();

  // Estados de vista y navegación
  const [modoVista, setModoVista] = useState<'mensual' | 'perspectiva'>('mensual');
  const [mesVisible, setMesVisible] = useState(() => new Date());
  const [capaActiva, setCapaActiva] = useState<CapaCalendario>('TODOS');
  const [eventoSeleccionado, setEventoSeleccionado] = useState<EventoCalendario | null>(null);

  // Formato YYYY-MM para la API
  const mesStr = `${mesVisible.getFullYear()}-${String(mesVisible.getMonth() + 1).padStart(2, '0')}`;

  const inicioMes = useMemo(
    () => new Date(Date.UTC(mesVisible.getFullYear(), mesVisible.getMonth(), 1, 12)),
    [mesVisible],
  );
  const finMes = useMemo(
    () => new Date(Date.UTC(mesVisible.getFullYear(), mesVisible.getMonth() + 1, 0, 12)),
    [mesVisible],
  );

  // Consultas canónicas
  const consolaQuery = useConsolaCalendario({
    mes: mesStr,
    capa: capaActiva,
  });

  const perspectivaQuery = usePerspectivaMultimes({
    mesReferencia: mesStr,
    cantidadMeses: 3,
    capa: capaActiva,
  });

  // Exportar calendario estándar .ics
  const handleExportarICS = async () => {
    try {
      const params = new URLSearchParams();
      if (capaActiva !== 'TODOS') params.set('capa', capaActiva);
      const qs = params.toString() ? `?${params.toString()}` : '';
      await descargar(`/calendario/exportar.ics${qs}`, 'lexicore-calendario.ics');
      avisosContext.exito('Calendario exportado a formato .ics correctamente.');
    } catch {
      avisosContext.error('No se pudo exportar el archivo .ics del calendario.');
    }
  };

  // Eventos agrupados por fecha (YYYY-MM-DD)
  const eventosPorDia = useMemo(() => {
    const mapa = new Map<string, EventoCalendario[]>();
    const lista = consolaQuery.data?.eventos ?? [];
    for (const evento of lista) {
      const clave = evento.fecha.slice(0, 10);
      mapa.set(clave, [...(mapa.get(clave) ?? []), evento]);
    }
    return mapa;
  }, [consolaQuery.data?.eventos]);

  // Construcción de celdas de la cuadrícula mensual
  const celdas = useMemo(() => {
    const desfase = diaSemanaLunesPrimero(inicioMes);
    const diasDelMes = finMes.getUTCDate();
    const hoy = aISO(new Date());

    const lista: { clave: string; numero: number | null; fecha: string | null; esHoy: boolean }[] = [];
    for (let i = 0; i < desfase; i++) {
      lista.push({ clave: `vacio-${i}`, numero: null, fecha: null, esHoy: false });
    }

    for (let dia = 1; dia <= diasDelMes; dia++) {
      const fecha = `${inicioMes.getUTCFullYear()}-${String(inicioMes.getUTCMonth() + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      lista.push({ clave: fecha, numero: dia, fecha, esHoy: fecha === hoy });
    }

    return lista;
  }, [inicioMes, finMes]);

  const kpis = consolaQuery.data?.kpis;
  const flujoNetoPositivo = (kpis?.flujoNeto ?? 0) >= 0;

  return (
    <>
      {/* Cabecera Principal del Módulo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 'var(--radius-sm)',
                background: 'var(--accent-blue-soft)',
                color: 'var(--accent-blue)',
              }}
            >
              <IconoCalendario size={16} />
            </span>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: '-0.01em' }}>
              Calendario & Cronograma Multimes
            </h1>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '4px 0 0 36px' }}>
            Cronograma vivo unificado de compromisos contractuales, flujos de caja y entregas operativas.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {/* Selector de Modo de Vista */}
          <div
            style={{
              display: 'flex',
              background: 'var(--bg-elevated)',
              padding: 3,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
            }}
          >
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setModoVista('mensual')}
              style={{
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: modoVista === 'mensual' ? 700 : 500,
                background: modoVista === 'mensual' ? 'var(--accent-blue-soft)' : 'transparent',
                color: modoVista === 'mensual' ? 'var(--accent-blue)' : 'var(--text-secondary)',
                border: 'none',
              }}
            >
              📅 Vista Mensual
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => setModoVista('perspectiva')}
              style={{
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: modoVista === 'perspectiva' ? 700 : 500,
                background: modoVista === 'perspectiva' ? 'var(--accent-blue-soft)' : 'transparent',
                color: modoVista === 'perspectiva' ? 'var(--accent-blue)' : 'var(--text-secondary)',
                border: 'none',
              }}
            >
              🔭 Perspectiva 3 Meses
            </button>
          </div>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleExportarICS}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Descargar archivo .ics compatible con Google Calendar, Apple Calendar y Outlook"
          >
            <IconoDescargar size={13} />
            <span>Exportar ICS</span>
          </button>
        </div>
      </div>

      {/* Barra de 4 KPIs de Flujo Neto del Mes */}
      {kpis && (
        <div className="kpi-grid" style={{ marginBottom: 20 }}>
          <TarjetaKpi
            etiqueta="Flujo Neto Proyectado"
            valor={formatearCLP(kpis.flujoNeto)}
            acento={flujoNetoPositivo ? 'var(--accent-green)' : 'var(--accent-red)'}
            colorValor={flujoNetoPositivo ? 'var(--accent-green)' : 'var(--accent-red)'}
            nota={flujoNetoPositivo ? 'Superávit estimado del período' : 'Déficit proyectado a cubrir'}
          />
          <TarjetaKpi
            etiqueta="Ingresos Estimados"
            valor={formatearCLP(kpis.ingresosEstimados.total)}
            acento="var(--accent-green)"
            colorValor="var(--accent-green)"
            nota={`${kpis.ingresosEstimados.facturasPorCobrarCount} facturas (${formatearCLP(kpis.ingresosEstimados.facturasPorCobrarMonto)}) + ${kpis.ingresosEstimados.cuotasProyectadasCount} cuotas`}
          />
          <TarjetaKpi
            etiqueta="Egresos Previstos"
            valor={formatearCLP(kpis.egresosPrevistos.total)}
            acento="var(--accent-red)"
            colorValor="var(--accent-red)"
            nota={`${kpis.egresosPrevistos.proveedoresPendientesCount} facturas prov. + ${kpis.egresosPrevistos.gastosFijosRecurrentesCount} recurrentes`}
          />
          <TarjetaKpi
            etiqueta="Entregas & Licitaciones"
            valor={`${kpis.operaciones.hitosCount} hitos`}
            acento="var(--accent-purple)"
            colorValor="var(--accent-purple)"
            nota={`${formatearCLP(kpis.operaciones.hitosMonto)} en hitos · ${kpis.operaciones.cierresLicitacionCount} cierres Mercado Público`}
          />
        </div>
      )}

      {/* Barra de Filtros de Capas Temáticas (1-Clic) */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          alignItems: 'center',
          flexWrap: 'wrap',
          background: 'var(--bg-elevated)',
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 6 }}>
          <IconoCapas size={14} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>Capas:</span>
        </div>

        {CAPAS.map((capa) => {
          const activo = capaActiva === capa.clave;
          return (
            <button
              key={capa.clave}
              type="button"
              className="btn btn-sm"
              onClick={() => setCapaActiva(capa.clave)}
              style={{
                background: activo ? 'var(--accent-blue-soft)' : 'transparent',
                color: activo ? 'var(--accent-blue)' : 'var(--text-secondary)',
                borderColor: activo ? 'var(--accent-blue)' : 'var(--border-subtle)',
                padding: '5px 12px',
                fontSize: 11.5,
                fontWeight: activo ? 700 : 500,
              }}
            >
              {capa.etiqueta}
            </button>
          );
        })}
      </div>

      {/* Contenido Principal: Alterna entre Vista Mensual y Perspectiva 3 Meses */}
      {modoVista === 'perspectiva' ? (
        /* ══════════════════════════════════════════════════════════════════════════
           VISTA PERSPECTIVA MULTIMES (3 MESES O TRIMESTRAL)
           ══════════════════════════════════════════════════════════════════════════ */
        perspectivaQuery.isLoading ? (
          <Cargando texto="Calculando perspectiva trimestral a 90 días…" />
        ) : perspectivaQuery.isError ? (
          <ErrorCarga error={perspectivaQuery.error} reintentar={() => void perspectivaQuery.refetch()} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Banner Resumen Trimestral */}
            {perspectivaQuery.data?.resumenTrimestre && (
              <div
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '14px 20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 16,
                }}
              >
                <div>
                  <h3 style={{ fontSize: 15, fontWeight: 800, margin: 0, color: 'var(--text-primary)' }}>
                    Balance Consolidado del Trimestre
                  </h3>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '3px 0 0' }}>
                    Proyección financiera para los próximos 90 días con cero carga manual.
                  </p>
                </div>

                <div style={{ display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 6 }}>Ingresos:</span>
                    <span className="td-mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-green)' }}>
                      {formatearCLP(perspectivaQuery.data.resumenTrimestre.ingresosEstimadosTotal)}
                    </span>
                  </div>

                  <div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 6 }}>Egresos:</span>
                    <span className="td-mono" style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent-red)' }}>
                      {formatearCLP(perspectivaQuery.data.resumenTrimestre.egresosPrevistosTotal)}
                    </span>
                  </div>

                  <div
                    style={{
                      background:
                        perspectivaQuery.data.resumenTrimestre.flujoNetoTotal >= 0
                          ? 'var(--accent-green-soft)'
                          : 'var(--accent-red-soft)',
                      padding: '5px 12px',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        color:
                          perspectivaQuery.data.resumenTrimestre.flujoNetoTotal >= 0
                            ? 'var(--accent-green)'
                            : 'var(--accent-red)',
                        fontWeight: 600,
                        marginRight: 6,
                      }}
                    >
                      Flujo Neto:
                    </span>
                    <span
                      className="td-mono"
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color:
                          perspectivaQuery.data.resumenTrimestre.flujoNetoTotal >= 0
                            ? 'var(--accent-green)'
                            : 'var(--accent-red)',
                      }}
                    >
                      {formatearCLP(perspectivaQuery.data.resumenTrimestre.flujoNetoTotal)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Las 3 Columnas Mensuales */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
              {perspectivaQuery.data?.meses.map((m) => {
                const netoMesPositivo = m.kpis.flujoNeto >= 0;
                return (
                  <Panel
                    key={m.mes}
                    titulo={m.nombreMes}
                    subtitulo={`${m.totalEventos} compromisos programados`}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {/* Flujo Neto del Mes */}
                      <div
                        style={{
                          background: netoMesPositivo ? 'var(--accent-green-soft)' : 'var(--accent-red-soft)',
                          border: `1px solid ${netoMesPositivo ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                          borderRadius: 'var(--radius-sm)',
                          padding: '10px 14px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                        }}
                      >
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                          Flujo Neto del Mes
                        </span>
                        <span
                          className="td-mono"
                          style={{
                            fontSize: 16,
                            fontWeight: 800,
                            color: netoMesPositivo ? 'var(--accent-green)' : 'var(--accent-red)',
                          }}
                        >
                          {formatearCLP(m.kpis.flujoNeto)}
                        </span>
                      </div>

                      {/* Ingresos vs Egresos */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 11.5 }}>
                        <div
                          style={{
                            padding: '8px 10px',
                            background: 'var(--bg-elevated)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-subtle)',
                          }}
                        >
                          <div style={{ color: 'var(--text-muted)' }}>Ingresos Estimados</div>
                          <div className="td-mono" style={{ fontWeight: 700, color: 'var(--accent-green)', marginTop: 2 }}>
                            {formatearCLP(m.kpis.ingresosEstimados.total)}
                          </div>
                        </div>

                        <div
                          style={{
                            padding: '8px 10px',
                            background: 'var(--bg-elevated)',
                            borderRadius: 'var(--radius-sm)',
                            border: '1px solid var(--border-subtle)',
                          }}
                        >
                          <div style={{ color: 'var(--text-muted)' }}>Egresos Previstos</div>
                          <div className="td-mono" style={{ fontWeight: 700, color: 'var(--accent-red)', marginTop: 2 }}>
                            {formatearCLP(m.kpis.egresosPrevistos.total)}
                          </div>
                        </div>
                      </div>

                      {/* Operaciones */}
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                        <strong>Operaciones:</strong> {m.kpis.operaciones.hitosCount} hitos a entregar (
                        {formatearCLP(m.kpis.operaciones.hitosMonto)}) · {m.kpis.operaciones.cierresLicitacionCount} cierres
                        de postulación.
                      </div>

                      {/* Días Clave del Mes */}
                      <div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                          Días Clave con Mayor Movimiento:
                        </span>
                        {m.diasClave.length === 0 ? (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            Sin concentraciones atípicas de caja.
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {m.diasClave.slice(0, 3).map((dc) => (
                              <div
                                key={dc.fecha}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  fontSize: 11,
                                  padding: '5px 8px',
                                  background: 'var(--bg-elevated)',
                                  borderRadius: 'var(--radius-sm)',
                                }}
                              >
                                <span style={{ fontFamily: 'var(--font-mono)' }}>{dc.fecha}</span>
                                <div style={{ display: 'flex', gap: 8 }}>
                                  {dc.montoIngresos > 0 && (
                                    <span style={{ color: 'var(--accent-green)', fontWeight: 600 }}>
                                      +{formatearCLP(dc.montoIngresos)}
                                    </span>
                                  )}
                                  {dc.montoEgresos > 0 && (
                                    <span style={{ color: 'var(--accent-red)', fontWeight: 600 }}>
                                      -{formatearCLP(dc.montoEgresos)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Botón para ver en vista mensual */}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          const partes = m.mes.split('-').map(Number);
                          const ano = partes[0] ?? new Date().getFullYear();
                          const mesNum = partes[1] ?? 1;
                          setMesVisible(new Date(ano, mesNum - 1, 1));
                          setModoVista('mensual');
                        }}
                        style={{ marginTop: 6, width: '100%', justifyContent: 'center' }}
                      >
                        <span>Abrir en Vista Mensual</span>
                        <IconoDerecha size={12} />
                      </button>
                    </div>
                  </Panel>
                );
              })}
            </div>
          </div>
        )
      ) : (
        /* ══════════════════════════════════════════════════════════════════════════
           VISTA MENSUAL TRADICIONAL CON CUADRÍCULA INTERACTIVA
           ══════════════════════════════════════════════════════════════════════════ */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 16, alignItems: 'start' }}>
          {/* Panel Principal: Cuadrícula Mensual */}
          <Panel>
            {/* Barra de Navegación del Mes */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 16,
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setMesVisible((m) => sumarMeses(m, -1))}
              >
                <IconoIzquierda size={13} />
                <span>Mes anterior</span>
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h3 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>
                  {NOMBRES_MES[inicioMes.getUTCMonth()]} {inicioMes.getUTCFullYear()}
                </h3>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setMesVisible(new Date())}
                >
                  Hoy
                </button>
              </div>

              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setMesVisible((m) => sumarMeses(m, 1))}
              >
                <span>Mes siguiente</span>
                <IconoDerecha size={13} />
              </button>
            </div>

            {consolaQuery.isLoading ? (
              <Cargando texto="Cargando calendario del mes…" />
            ) : consolaQuery.isError ? (
              <ErrorCarga error={consolaQuery.error} reintentar={() => void consolaQuery.refetch()} />
            ) : (
              <>
                {/* Cabecera de Días de la Semana */}
                <div className="cal-grid" style={{ marginBottom: 6 }}>
                  {NOMBRES_DIA_CORTOS.map((dia) => (
                    <div key={dia} className="cal-day-name">
                      {dia}
                    </div>
                  ))}
                </div>

                {/* Grilla de Días y Eventos */}
                <div className="cal-grid">
                  {celdas.map((celda) => {
                    const delDia = celda.fecha ? eventosPorDia.get(celda.fecha) ?? [] : [];

                    return (
                      <div
                        key={celda.clave}
                        className={`cal-day${celda.numero === null ? ' vacio' : ''}${celda.esHoy ? ' hoy' : ''}`}
                        style={{ minHeight: 92 }}
                      >
                        {celda.numero !== null && (
                          <>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 4,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 800,
                                  fontSize: 11.5,
                                  color: celda.esHoy ? 'var(--accent-blue)' : 'var(--text-secondary)',
                                }}
                              >
                                {celda.numero}
                              </span>

                              {delDia.length > 0 && (
                                <span
                                  style={{
                                    fontSize: 9.5,
                                    color: 'var(--text-muted)',
                                    fontWeight: 600,
                                  }}
                                >
                                  {delDia.length} {delDia.length === 1 ? 'evt' : 'evts'}
                                </span>
                              )}
                            </div>

                            {/* Lista de Eventos del Día */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                              {delDia.slice(0, 3).map((evento) => {
                                const fondoColor = FONDOS[evento.tipo] || 'var(--accent-blue-soft)';
                                const textoColor = COLOR_TIPO_EVENTO[evento.tipo as TipoEvento] || 'var(--accent-blue)';

                                return (
                                  <div
                                    key={evento.id}
                                    className="cal-event"
                                    onClick={() => setEventoSeleccionado(evento)}
                                    title={`${ETIQUETAS_TIPO_EVENTO[evento.tipo as TipoEvento] || evento.tipo}: ${evento.titulo}${evento.monto ? ` · ${formatearCLP(evento.monto)}` : ''}`}
                                    style={{
                                      background: fondoColor,
                                      color: textoColor,
                                      cursor: 'pointer',
                                      padding: '3px 5px',
                                      fontSize: 10,
                                      lineHeight: 1.25,
                                      borderRadius: 4,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      transition: 'transform 0.1s ease',
                                    }}
                                  >
                                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {evento.titulo}
                                    </div>
                                    {evento.monto !== null && (
                                      <div
                                        style={{
                                          fontFamily: 'var(--font-mono)',
                                          fontSize: 9.5,
                                          fontWeight: 700,
                                          opacity: 0.9,
                                        }}
                                      >
                                        {formatearCLP(evento.monto)}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}

                              {delDia.length > 3 && (
                                <div
                                  style={{
                                    fontSize: 9.5,
                                    color: 'var(--accent-blue)',
                                    fontWeight: 600,
                                    marginTop: 2,
                                    cursor: 'pointer',
                                  }}
                                  onClick={() => {
                                    if (delDia[3]) setEventoSeleccionado(delDia[3]);
                                  }}
                                >
                                  +{delDia.length - 3} compromisos más
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </Panel>

          {/* Panel Lateral: Avisos Urgentes (Próximos 7 días / 48 horas) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Panel
              titulo="Avisos Críticos de Agenda"
              subtitulo="Compromisos con vencimiento en las próximas 48 horas o 7 días"
            >
              {consolaQuery.data?.avisosProximos && consolaQuery.data.avisosProximos.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {consolaQuery.data.avisosProximos.map((aviso) => {
                    const colorBorde = aviso.esUrgente48h ? 'var(--accent-red)' : 'var(--accent-yellow)';
                    const fondoAviso = aviso.esUrgente48h ? 'var(--accent-red-soft)' : 'var(--accent-yellow-soft)';

                    return (
                      <div
                        key={aviso.id}
                        style={{
                          background: fondoAviso,
                          border: `1px solid ${colorBorde}`,
                          borderRadius: 'var(--radius-sm)',
                          padding: '10px 12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 6,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 800,
                              color: colorBorde,
                              textTransform: 'uppercase',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                            }}
                          >
                            <IconoReloj size={11} />
                            {aviso.esUrgente48h ? 'Vence en 48h' : 'Próximos 7 días'}
                          </span>
                          <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {aviso.fecha.slice(0, 10)}
                          </span>
                        </div>

                        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>
                          {aviso.titulo}
                        </div>

                        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                          {aviso.subtitulo}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                          {aviso.monto !== null ? (
                            <span className="td-mono" style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--text-primary)' }}>
                              {formatearCLP(aviso.monto)}
                            </span>
                          ) : <div />}

                          {aviso.accionSugerida && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                const r = aviso.accionSugerida!.ruta;
                                if (r.startsWith('/gastos')) navegar('/costos');
                                else if (r.startsWith('/facturas')) navegar('/cobranza');
                                else if (r.startsWith('/licitaciones')) navegar('/pipeline');
                                else navegar(r);
                              }}
                              style={{
                                padding: '4px 8px',
                                fontSize: 10.5,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                              }}
                            >
                              <span>{aviso.accionSugerida.etiqueta}</span>
                              <IconoDerecha size={11} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <Vacio
                  titulo="Sin urgencias pendientes"
                  detalle="No hay compromisos con vencimiento crítico para los próximos 7 días."
                />
              )}
            </Panel>
          </div>
        </div>
      )}

      {/* Modal Contextual de Acción 1-Clic para el Evento Seleccionado */}
      {eventoSeleccionado && (
        <ModalDetalleEventoCalendario
          evento={eventoSeleccionado}
          alCerrar={() => setEventoSeleccionado(null)}
        />
      )}
    </>
  );
}
